import { useCallback, useEffect, useRef } from "react";
import type { VoicePhase } from "@/features/celebrity/types";

interface UseVoicePhasesOptions {
  phases: VoicePhase[];
  /** Interval in seconds between voice clips. Default 15. */
  intervalSec?: number;
  enabled: boolean;
}

/**
 * Plays celebrity voice clips during a workout.
 *
 * Clips play in their admin-defined order, one at a time on the configured
 * cadence, and stop after the last clip — they are never repeated.
 */
export function useVoicePhases({ phases, intervalSec = 15, enabled }: UseVoicePhasesOptions) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const indexRef = useRef(0);
  const pendingNextRef = useRef(false);

  // Latest values live in refs so the callbacks stay stable and the playback
  // loop is never restarted by unrelated re-renders (e.g. per-frame pose
  // updates or the 500ms elapsed-time tick while the workout is active).
  const phasesRef = useRef(phases);
  const intervalSecRef = useRef(intervalSec);
  phasesRef.current = phases;
  intervalSecRef.current = intervalSec;

  const playNext = useCallback(() => {
    const sorted = [...phasesRef.current].sort((a, b) => a.order - b.order);
    if (sorted.length === 0) return;

    // All clips have played — stop (no repeating).
    if (indexRef.current >= sorted.length) {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.onended = null;
        audioRef.current.pause();
        audioRef.current = null;
      }
      indexRef.current = 0;
      return;
    }

    // Stop any currently playing clip
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.pause();
      audioRef.current = null;
    }

    const phase = sorted[indexRef.current];
    indexRef.current += 1;
    if (!phase?.audioDataUrl) return;

    const audio = new Audio(phase.audioDataUrl);
    audio.volume = 0.7;
    // Let the clip play to the end. If a tick arrived mid-clip, start the next
    // one as soon as this one finishes instead of cutting it short.
    audio.onended = () => {
      audioRef.current = null;
      if (pendingNextRef.current) {
        pendingNextRef.current = false;
        playNext();
      }
    };
    audio.play().catch(() => {
      // Autoplay may be blocked — silently fail
    });
    audioRef.current = audio;
  }, []);

  const stopPlayback = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
    pendingNextRef.current = false;
    indexRef.current = 0;
  }, []);

  const startPlayback = useCallback(() => {
    stopPlayback();
    const sorted = [...phasesRef.current].sort((a, b) => a.order - b.order);
    if (sorted.length === 0) return;

    // Play the first clip immediately
    playNext();

    // Then aim for one clip per interval. Ticks that land mid-clip queue the
    // next clip to play right after the current one ends.
    timerRef.current = setInterval(() => {
      const audio = audioRef.current;
      if (audio && !audio.paused && !audio.ended) {
        pendingNextRef.current = true;
        return;
      }
      playNext();
    }, intervalSecRef.current * 1000);
  }, [stopPlayback, playNext]);

  // Auto-start/stop based on enabled flag
  useEffect(() => {
    if (enabled) {
      startPlayback();
    } else {
      stopPlayback();
    }
    return stopPlayback;
  }, [enabled, startPlayback, stopPlayback]);

  return { startPlayback, stopPlayback, skipToNext: playNext };
}
